import { BroadcastMessageType } from '../types'
import { hasPasskeyOnDevice } from '../utils/passkeyDevice'
import type { Brand as UserAuthBrand } from '@core/types/clients/user-auth'
import type { MaybeRefOrGetter } from '#imports'

import {
    ref,
    computed,
    useCookie,
    useRequestURL,
    useReload,
    useDayjs,
    useUserInfo,
    useCoreConfig,
    useDevice,
    useSharedBroadcast,
    useQuery,
    useUserAuthClient,
    useTracking,
    createSharedComposable,
    OpenapiError
} from '#imports'

export type WebAuthnAuthFailureReason = 'KEY_NOT_FOUND' | 'GENERIC' | 'NO_OPTIONS'
export type WebAuthnAuthResult = { ok: true } | { ok: false, reason: WebAuthnAuthFailureReason }

// Coalesces concurrent authentication-options fetches. On app load two callers can
// race before the first response lands: the web CREATED-watcher and a native
// app-injected window.passKeyFunctions.initiatePassKeyLogin(). The value-cache can't
// dedupe them (authenticationOptions.value isn't set until the response returns), so
// both would fire the request. Module-scoped so it survives shared-composable churn.
let authOptionsInFlight: Promise<unknown> | null = null

export const useWebauthn = createSharedComposable(() => {
    const { site } = useCoreConfig()
    const { userJustLoggedIn } = useUserInfo()
    const config = useCoreConfig()
    const dayjs = useDayjs()
    const url = useRequestURL()

    const { userAgent, isIos, isAndroid } = useDevice()
    const { sendMessage } = useSharedBroadcast()

    const { client } = useUserAuthClient()
    const reload = useReload()
    const { trackEvent } = useTracking()

    // State - WebAuthn Core
    const authenticationOptions = ref()
    const registrationOptions = ref()
    const registrationState = ref<{
        loading: boolean
        error: string | null
        isRegistered: boolean
    }>({
        loading: false,
        error: null,
        isRegistered: false
    })

    // True once a passkey login has authenticated and kicked off the reload.
    // Lets the login flow skip the dismiss/error fallback navigation on success,
    // which would otherwise momentarily render the logged-in state before reload.
    const passkeyLoginSucceeded = ref(false)

    // State - UI
    const isModalShown = ref(false)

    // State - Cookies and persistence
    const passkeyStatus = useCookie<string | null>('passkey_status', {
        maxAge: dayjs.duration({ days: 30 }).asSeconds(),
    })

    // State - Web passkey support
    const webPasskeySupported = ref(false)

    if (import.meta.client && config.webauthn?.enabled) {
        (async () => {
            try {
                const supported = await isPlatformPassKeySupported()
                webPasskeySupported.value = !!supported
            }
            catch {
                webPasskeySupported.value = false
            }
        })()
    }

    // Computed properties
    const canLoginWithWebPasskey = computed(() => {
        return webPasskeySupported.value && passkeyStatus.value === 'registered'
    })

    // Core WebAuthn Functions
    async function isPlatformPassKeySupported() {
        const { browserSupportsWebAuthn, platformAuthenticatorIsAvailable } = await import('@simplewebauthn/browser')
        return config.webauthn?.enabled && browserSupportsWebAuthn() && await platformAuthenticatorIsAvailable()
    }

    async function fetchRegistrationOptions(options?: { force: boolean }) {
        const { force = false } = options || {}
        // Return cached options if available and not forcing refresh
        if (registrationOptions.value && !force) {
            return registrationOptions.value
        }

        const res = await client.GET('/v1/public/pass-key/registration-options', {
            params: {
                query: {
                    brand: site.brand as UserAuthBrand
                }
            },
        })
        registrationOptions.value = res?.data

        return res?.data
    }

    function clearRegistrationOptions() {
        registrationOptions.value = undefined
    }

    function clearAuthenticationOptions() {
        authenticationOptions.value = undefined
    }

    async function verifyRegistration(attResp: any): Promise<boolean> {
        registrationState.value.error = null

        try {
            await client.POST('/v1/public/pass-key/verify-registration', {
                body: attResp
            })
        }
        catch {
            await trackEvent({
                event_name: 'passkey_register_failed',
                event_type: 'Click',
                meta_data: [
                    { category: 'Passkey' },
                ],
            })

            markPassKeyAsRejected()
            return false
        }

        await trackEvent({
            event_name: 'passkey_register_completed',
            event_type: 'Click',
            meta_data: [
                { category: 'Passkey' },
            ],
        })

        markPassKeyAsRegistered()
        return true
    }

    async function registerPassKey() {
        registrationState.value.error = null

        await trackEvent({
            event_name: 'passkey_register',
            event_type: 'Click',
            meta_data: [
                { category: 'Passkey' },
            ],
        })

        const options: any = await fetchRegistrationOptions()

        try {
            const { startRegistration } = await import('@simplewebauthn/browser')
            const attResp = await startRegistration({ optionsJSON: options })
            await verifyRegistration(attResp)
        }
        catch (err: any) {
            if (err.code === 'ERROR_AUTHENTICATOR_PREVIOUSLY_REGISTERED') {
                await trackEvent({
                    event_name: 'passkey_register_completed',
                    event_type: 'Click',
                    meta_data: [
                        { category: 'Passkey' },
                    ],
                })

                markPassKeyAsRegistered()
            }
            else {
                await trackEvent({
                    event_name: 'passkey_register_failed',
                    event_type: 'Click',
                    meta_data: [
                        { category: 'Passkey' },
                    ],
                })

                markPassKeyAsRejected()
            }

            closeModal()
        }
    }

    async function getAuthenticationOptions() {
        if (authenticationOptions.value) {
            return authenticationOptions.value
        }

        // Concurrent callers share the one in-flight request instead of each firing their own.
        if (authOptionsInFlight) {
            return authOptionsInFlight
        }

        authOptionsInFlight = (async () => {
            try {
                const res = await client.GET('/v1/public/pass-key/authentication-options', {
                    params: {
                        query: {
                            brand: site.brand as UserAuthBrand
                        }
                    },
                })
                authenticationOptions.value = res?.data
                return authenticationOptions.value
            }
            catch {
                // Do nothing
            }
            finally {
                authOptionsInFlight = null
            }
        })()

        return authOptionsInFlight
    }

    async function authenticateWithWebAuthn(authOptions: any): Promise<WebAuthnAuthResult> {
        registrationState.value.error = null

        if (!authOptions) {
            registrationState.value.error = 'core.defaultErrorMessage'
            setLoadingState(false)
            clearAuthenticationOptions()
            return { ok: false, reason: 'NO_OPTIONS' }
        }

        try {
            await client.POST('/v1/public/pass-key/verify-authentication', {
                body: {
                    ...authOptions,
                    brand: site.brand
                },
            })
        }
        catch (err) {
            let reason: WebAuthnAuthFailureReason = 'GENERIC'
            if (OpenapiError.isError(err) && err.status === 404 && (err.data as { message?: string })?.message === 'KEY_NOT_FOUND') {
                passkeyStatus.value = null
                registrationState.value.error = 'core.webauthn.keyNotFound'
                reason = 'KEY_NOT_FOUND'
            }
            else {
                registrationState.value.error = 'core.defaultErrorMessage'
            }
            setLoadingState(false)
            clearAuthenticationOptions()

            await trackEvent({
                event_name: 'login_passkey_failed',
                event_type: 'Click',
                meta_data: [
                    { category: 'Login' },
                ],
            })

            return { ok: false, reason }
        }

        await trackEvent({
            event_name: 'login_passkey_completed',
            event_type: 'Click',
            meta_data: [
                { category: 'Login' },
            ],
        })

        userJustLoggedIn.value = true
        passkeyLoginSucceeded.value = true
        await reload(`${url.origin}${url.pathname}`)
        return { ok: true }
    }

    async function initiatePassKeyLogin() {
        passkeyLoginSucceeded.value = false

        await trackEvent({
            event_name: 'login_passkey',
            event_type: 'Click',
            meta_data: [
                { category: 'Login' },
            ],
        })

        try {
            registrationState.value.error = null

            const authenticationOptions = await getAuthenticationOptions()

            if (!authenticationOptions) {
                registrationState.value.error = 'core.defaultErrorMessage'
                setLoadingState(false)
                return
            }

            const isSupported = await isPlatformPassKeySupported()

            if (!isSupported) {
                setLoadingState(false)
                return
            }

            const { startAuthentication: startAuth } = await import('@simplewebauthn/browser')
            await startAuth({ optionsJSON: authenticationOptions }).then(authenticateWithWebAuthn)
        }
        catch (err) {
            // User dismissed the native sheet (or the platform rate-limited a back-to-back
            // call). Chromium reports this as NotAllowedError, other engines as AbortError.
            // Neither is a real failure - stay silent and just let the login dialog open.
            const isUserCancellation = err instanceof Error
                && (err.name === 'AbortError' || err.name === 'NotAllowedError')

            if (!isUserCancellation) {
                registrationState.value.error = 'core.defaultErrorMessage'
            }

            await trackEvent({
                event_name: 'login_passkey_failed',
                event_type: 'Click',
                meta_data: [
                    { category: 'Login' },
                ],
            })

            setLoadingState(false)
            clearAuthenticationOptions()
        }
    }

    // Data fetching
    function useGetPasskeys(options: { lazy?: boolean, server?: boolean, enabled?: MaybeRefOrGetter<boolean> } = {}) {
        const { lazy = true, server = false, enabled = true } = options

        const query = useQuery(async ({ signal }) => {
            const response = await client.GET('/v1/public/pass-key', { signal })
            return response
        }, {
            key: 'webauthn:passkeys',
            lazy,
            server,
            enabled,
        })

        return query
    }

    // UI Management Functions
    function closeModal() {
        registrationState.value.error = null
        isModalShown.value = false
        document.dispatchEvent(new CustomEvent('closePassKeyModal'))
    }

    function showPassKeysModal() {
        document.dispatchEvent(new CustomEvent('showPassKeysModal'))
        isModalShown.value = true
    }

    // State Management Functions
    function markPassKeyAsRegistered() {
        passkeyStatus.value = 'registered'
        registrationState.value.isRegistered = true

        // Clear registration options to ensure fresh ones are fetched next time
        clearRegistrationOptions()

        sendMessage({ type: BroadcastMessageType.PASSKEY_REGISTERED })

        setTimeout(() => {
            closeModal()
        }, 5000)
    }

    function markPassKeyAsRejected(customErrorMessage?: string) {
        registrationState.value.error = customErrorMessage ?? 'core.webauthn.registrationDialog.errorMessage'
        passkeyStatus.value = null

        sendMessage({ type: BroadcastMessageType.PASSKEY_REJECTED })
    }

    async function checkAndRestorePasskeyStatus(): Promise<boolean> {
        if (passkeyStatus.value === 'registered') return true

        try {
            const response = await client.GET('/v1/public/pass-key', {})
            if (hasPasskeyOnDevice(userAgent, isIos, isAndroid, response?.data || [])) {
                passkeyStatus.value = 'registered'
                return true
            }
        }
        catch {
            // Silently fail
        }
        return false
    }

    function markPassKeyAsDismissed() {
        passkeyStatus.value = 'dismissed'
        closeModal()
    }

    function setLoadingState(loading: boolean) {
        registrationState.value.loading = loading
    }

    async function doStartAuthentication(options: any) {
        const { startAuthentication } = await import('@simplewebauthn/browser')
        return startAuthentication({ optionsJSON: options })
    }

    return {
        // State
        authenticationOptions,
        registrationOptions,
        registrationState,
        isModalShown,
        passkeyStatus,
        passkeyLoginSucceeded,

        // Computed
        canLoginWithWebPasskey,

        // Core WebAuthn functions
        fetchRegistrationOptions,
        clearRegistrationOptions,
        verifyRegistration,
        registerPassKey,
        getAuthenticationOptions,
        authenticateWithWebAuthn,
        isPlatformPassKeySupported,
        startAuthentication: doStartAuthentication,
        initiatePassKeyLogin,
        useGetPasskeys,

        // UI functions
        closeModal,
        showPassKeysModal,

        // State management
        markPassKeyAsRegistered,
        markPassKeyAsRejected,
        markPassKeyAsDismissed,
        checkAndRestorePasskeyStatus,
        setLoadingState,
    }
})
